import { useState } from 'react'
import { ReactionBar } from './ReactionBar'
import { messageOf } from '../lib/errors'
import {
  editRecognition, editWindowMinutes, stillEditable,
  withdrawRecognition, STAR_PRESETS, type Endorsement, type WallItem,
} from '../lib/stars'
import { EndorsementField } from './EndorsementField'
import { useHubLang } from '../lib/i18n'
import { shownIn, STARS } from '../lib/hubCopy'
import type { HubLang } from '../lib/i18n'

/**
 * One recognition on the wall. REC-4's card, rendered by REC-7's screen.
 *
 * It lives in components/ rather than inside Stars.tsx for a reason worth
 * keeping: importing the ROUTE pulls in AppShell, which imports an SVG, which
 * the test bundler cannot resolve — so a card defined in the route file is a
 * card the suite physically cannot render. That is how a screen ends up with
 * 357 passing assertions and no coverage of the thing just built.
 *
 * ── A SHOUT-OUT IS THIS CARD WITH NO STAR PILL ──────────────────────────────
 *
 * Not a lesser thing in a separate list. `stars > 0` gates the pill and
 * NOTHING else about the row — same size, same position, same actions. 061
 * made a shout-out one row in one table for the same reason; a second card
 * here would put the distinction back in the interface after the schema had
 * removed it.
 *
 * ── NO CASH, AND NO RANKING ─────────────────────────────────────────────────
 *
 * D13 and D10. No currency is rendered anywhere (the 1c figure is a planning
 * guide in a spreadsheet), and there is no position, rank or "top" anything.
 * Both are asserted in the suite, because both are the kind of thing that
 * arrives later as a small helpful addition.
 *
 * ── THE WINDOW NOTE IS SHOWN TO THE AUTHOR AND NOBODY ELSE ──────────────────
 *
 * REC-19, and David's words for it: a small note saying "Editable for 15
 * mins". It is not a countdown and should not become one — a timer ticking
 * down on a thank-you turns a nice thing into a deadline. The FIFTEEN comes
 * from the row (`editWindowMinutes`), never from a constant here, because
 * `star_policy.edit_window` is a column and a literal in this file would
 * survive a change to it and start lying.
 *
 * Everyone else sees the card exactly as before. The held stars are not
 * disclosed on the wall either: `★ 100` is what was given, which is true from
 * the moment it is given, and whether it has matured is the recipient's own
 * business and lives on their balance.
 */
export function RecognitionCard({
  item,
  featured = [],
  onChanged,
}: {
  item: WallItem
  /** The give form's preset chips, for the in-place editor (128). */
  featured?: Endorsement[]
  onChanged?: () => void | Promise<void>
}) {
  const lang = useHubLang()
  const t = STARS[lang]
  const [editing, setEditing] = useState(false)
  const mine = stillEditable(item)

  if (editing) {
    return (
      <RecognitionEditor
        item={item}
        featured={featured}
        onDone={async () => {
          setEditing(false)
          await onChanged?.()
        }}
        onCancel={() => setEditing(false)}
      />
    )
  }

  return (
    <li className="stars__item">
      <span className="avatar" aria-hidden="true">{initialsOf(item.giverName)}</span>
      <div className="stars__itemmain">
        <p className="stars__who">
          <strong>{item.giverName}</strong>{t.recognizedJoin}<strong>{item.personName}</strong>
        </p>
        <p className="stars__meta">
          {/* 128: what it was for, as the server labelled it for this reader.
              An official word and one somebody coined look the same here on
              purpose — the wall is people's words, not a ranking of them. */}
          {item.endorsements.map((e) => (
            <span className="pill" key={e.id}>{e.label}</span>
          ))}
          <span className="post__when">{starWhen(item.publishedAt, lang)}</span>
          {item.isFallback && (
            <span className="pill" title={shownIn(lang, item.variantLocale)}>
              {item.variantLocale}
            </span>
          )}
          {mine && (
            <span className="stars__window">
              {t.editableFor(editWindowMinutes(item))}
            </span>
          )}
        </p>
        <p className="stars__body">{item.body}</p>
        <div className="stars__foot">
          <ReactionBar
            target={{ contentId: item.contentId }}
            counts={item.reactions}
            mine={item.myReaction}
            compact
          />
          {mine && onChanged && (
            <AuthorActions item={item} onDone={onChanged} onEdit={() => setEditing(true)} />
          )}
          {item.stars > 0 && <span className="pill pill--star">★ {item.stars}</span>}
        </div>
      </div>
    </li>
  )
}

/**
 * Edit and Take down, and the asymmetry is 030's: the reversible one asks for
 * nothing, the irreversible one confirms. Take down is NOT `.btn--danger` —
 * that is reserved for a moderator acting on somebody else's words, and this
 * is a person unsending their own.
 */
function AuthorActions({
  item,
  onDone,
  onEdit,
}: {
  item: WallItem
  onDone: () => void | Promise<void>
  onEdit: () => void
}) {
  const t = STARS[useHubLang()]
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function takeDown() {
    if (busy) return
    // The stars go back to the author's allowance, which is the part people do
    // not expect and the part worth saying before the click.
    const ok = window.confirm(
      item.stars > 0
        ? t.confirmTakeDownStars(item.personName, item.stars)
        : t.confirmTakeDown,
    )
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      await withdrawRecognition(item.contentId)
      await onDone()
    } catch (e) {
      setError(messageOf(e))
      setBusy(false)
    }
  }

  return (
    <span className="stars__own">
      <button type="button" className="btn btn--quiet btn--xs" onClick={onEdit} disabled={busy}>
        {t.edit}
      </button>
      <button type="button" className="btn btn--quiet btn--xs" onClick={takeDown} disabled={busy}>
        {busy ? t.takingDown : t.takeDown}
      </button>
      {error && <span className="composer__error" role="alert">{error}</span>}
    </span>
  )
}

/**
 * The editor is the give form's three fields and nothing else — who is
 * deliberately absent, because the server takes no recipient argument and
 * offering the field here would promise something the function refuses.
 *
 * It replaces the card in place rather than opening beside it: two copies of
 * one recognition on screen at once is how somebody edits the wrong one.
 */
function RecognitionEditor({
  item,
  featured,
  onDone,
  onCancel,
}: {
  item: WallItem
  featured: Endorsement[]
  onDone: () => void | Promise<void>
  onCancel: () => void
}) {
  const lang = useHubLang()
  const t = STARS[lang]
  const [endorsements, setEndorsements] = useState<string[]>(item.endorsements.map((e) => e.label))
  const [body, setBody] = useState(item.body)
  const [amount, setAmount] = useState(item.stars)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The presets, plus whatever this recognition already carries — a row given
  // 75 stars before the presets changed must not silently become 50 because
  // its own amount is not on the list.
  const presets = Array.from(new Set<number>([...STAR_PRESETS, item.stars])).sort((a, b) => a - b)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (busy || !body.trim() || endorsements.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await editRecognition({
        contentId: item.contentId,
        endorsements,
        body: body.trim(),
        stars: amount,
      })
      await onDone()
    } catch (e) {
      setError(messageOf(e))
      setBusy(false)
    }
  }

  return (
    <li className="stars__item">
      <span className="avatar" aria-hidden="true">{initialsOf(item.giverName)}</span>
      <form className="stars__itemmain stars__give" onSubmit={save}>
        <p className="stars__who">
          {t.editingRecognition}<strong>{item.personName}</strong>
        </p>

        <EndorsementField featured={featured} value={endorsements} onChange={setEndorsements} />

        <label className="stars__field">
          <span className="composer__label">{t.inYourWords}</span>
          <textarea
            className="composer__body"
            rows={3}
            maxLength={2000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>

        <fieldset className="stars__field">
          <legend className="composer__label">
            {t.addStars} <span className="composer__optional">{t.optional}</span>
          </legend>
          <div className="chips">
            {presets.map((n) => (
              <button
                key={n}
                type="button"
                className={`chip${amount === n ? ' chip--on' : ''}`}
                aria-pressed={amount === n}
                onClick={() => setAmount(n)}
              >
                {n === 0 ? t.none : `★ ${n}`}
              </button>
            ))}
          </div>
        </fieldset>

        {error && <p className="composer__error" role="alert">{error}</p>}

        <div className="composer__actions">
          <button className="btn btn--primary btn--sm" type="submit" disabled={busy || !body.trim() || endorsements.length === 0}>
            {busy ? t.saving : t.save}
          </button>
          <button type="button" className="btn btn--quiet btn--sm" onClick={onCancel} disabled={busy}>
            {t.cancel}
          </button>
        </div>
      </form>
    </li>
  )
}

/**
 * Coarse, like the admin screen's dates and for the same reason (044).
 * Exported for the earnings list (106), so a recognition's age reads the same
 * on the wall and in "Stars you earned".
 */
export function starWhen(iso: string, lang: HubLang): string {
  const t = STARS[lang]
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return t.today
  if (days === 1) return t.yesterday
  if (days < 7) return t.daysAgo(days)
  if (days < 30) return t.weeksAgo(Math.floor(days / 7))
  return new Date(iso).toLocaleDateString(lang === 'es' ? 'es' : undefined)
}

// Duplicated from five other files. Not consolidated here because doing it
// properly is one shared helper and six edits, which is its own commit —
// filed as SHL-12 rather than half-done in the middle of a feature.
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}
